import { api, qs } from "./base";
import type { ExceptionRow, FeedbackRow, ListResponse } from "@/common/types";

/** Exceptions by repo, state, or REQ#. */
export function listExceptions(params: {
  repo?: string;
  state?: string;
  req?: string;
}): Promise<ListResponse<ExceptionRow>> {
  return api.get<ListResponse<ExceptionRow>>(`/exceptions${qs(params)}`);
}

export function getException(exceptionId: string): Promise<ExceptionRow> {
  return api.get<ExceptionRow>(`/exceptions/detail${qs({ exceptionId })}`);
}

// -- write actions (actor recorded for audit until OKTA scopes carry the subject) --

export function approveException(exceptionId: string, actor: string) {
  return api.post(`/exceptions/approve${qs({ exceptionId })}`, { actor });
}

export function rejectException(
  exceptionId: string,
  actor: string,
  reason: string,
) {
  return api.post(`/exceptions/reject${qs({ exceptionId })}`, {
    actor,
    reason,
  });
}

export function withdrawException(exceptionId: string, actor: string) {
  return api.post(`/exceptions/withdraw${qs({ exceptionId })}`, { actor });
}

/**
 * Change the ServiceNow number recorded on a repo's exceptions, in place — typically an RITM id
 * swapped for the REQ number once ServiceNow assigns it. Same ticket: the filed date and the
 * 90-day re-evaluation date do not move. Rejects (409) if `toReq` is already recorded for the
 * repo, so two tickets are never merged by accident.
 */
export function updateRequestNumber(
  actor: string,
  body: { repo: string; fromReq: string; toReq: string },
) {
  return api.post<{
    ok: boolean;
    repo: string;
    fromReq: string;
    toReq: string;
    updated: number;
    cveIds: string[];
  }>("/exceptions/update-req", { actor, ...body });
}

/**
 * Replace a stale REQ with a newly filed one. The CVEs under `fromReq` that are still on the
 * image move to `toReq` with ITS filing date (the 90-day clock restarts), and `fromReq` is
 * retired. CVEs that have cleared are not carried over. This is a different ticket, not a
 * relabel - use updateRequestNumber for the same ticket under a new number.
 */
export function renewRequest(
  actor: string,
  body: { repo: string; fromReq: string; toReq: string; filedDate: string },
) {
  return api.post<{
    ok: boolean;
    fromReq: string;
    toReq: string;
    filedDate: string;
    expiresAt: number;
    renewed: string[];
    clearedNotCarried: string[];
    retired: number;
  }>("/exceptions/renew-req", { actor, ...body });
}

/**
 * Retire every live exception under a REQ for this repo (nothing left to cover, or recorded in
 * error). vuln-sentinel bookkeeping only - ServiceNow is never touched. Any CVE it still
 * covered goes back to needing action.
 */
export function retireRequest(
  actor: string,
  body: { repo: string; req: string; reason?: string; cveIds?: string[] },
) {
  return api.post<{
    ok: boolean;
    req: string;
    retired: number;
    cveIds: string[];
  }>("/exceptions/retire-req", { actor, ...body });
}

/**
 * Attach a pre-existing, externally-filed ServiceNow REQ to a finding's CVE(s), so
 * vuln-sentinel suppresses it. filedDate ('YYYY-MM-DD') anchors the 90-day re-eval window.
 */
export function attachException(
  actor: string,
  body: {
    repo: string;
    cveIds: string[];
    reqNumber: string;
    filedDate: string;
    findingArns?: string[];
    component?: string;
    groupKey?: string;
  },
) {
  return api.post<{
    ok: boolean;
    reqNumber: string;
    count: number;
    expiresAt: number;
  }>("/exceptions/attach", { actor, ...body });
}

// -- feedback (M4 Phase 2 — learning-loop input) --

/** All feedback for one exception PR (keyed by its prUrl as subjectId). */
export function listExceptionFeedback(
  subjectId: string,
): Promise<ListResponse<FeedbackRow>> {
  return api.get<ListResponse<FeedbackRow>>(`/feedback${qs({ subjectId })}`);
}

/** Submit engineer feedback on an exception. outcome ∈ APPROVED|REJECTED. subjectId = exception prUrl. */
export function submitExceptionFeedback(
  subjectId: string,
  repo: string,
  actor: string,
  outcome: string,
  comment: string,
) {
  return api.post(`/exceptions/feedback${qs({ exceptionId: subjectId })}`, {
    repo,
    outcome,
    comment,
    actor,
  });
}

/** The staleness rule the server enforces (REQ valid `expiryDays` from filing, warned `warnDays` before). */
export function getExceptionPolicy(): Promise<{
  expiryDays: number;
  warnDays: number;
}> {
  return api.get<{ expiryDays: number; warnDays: number }>(
    "/exceptions/policy",
  );
}
